/* ----------------------------------------------------------------------------
 * leads-config.ts — the ONE central option config for lead enumerations:
 * booking-platform kinds, visit approaches, sales statuses, priorities.
 *
 * Every label/option the FE shows for these (cards, detail, edit form, filters,
 * import preview) is read from here — never hard-coded in a component. The
 * server mirrors the VALUE lists for validation in
 * bookedcentral-api/modules/outreach/Services/ImportFieldMap.php
 * (PLATFORM_KINDS / PLATFORM_KIND_LABELS / PLATFORM_KIND_HELP, VISIT_APPROACH,
 * SETTABLE_STATUSES / STATUS_LABELS, PRIORITIES). Values are stable enum tokens
 * (what is stored); labels/help are display-only and may change freely.
 *
 * Tenant-agnostic by construction: nothing here looks at a workspace id
 * (governance C4). The BookSesh wording below is the current DEFAULT. A later
 * per-workspace source (bc_workspace_settings) plugs in through
 * resolveLeadOptions(overrides) — callers keep reading LEAD_OPTIONS-shaped
 * objects, so no component rewrite is needed. Building that per-workspace
 * source is a separate architecture item (ADR), deliberately NOT done here.
 * -------------------------------------------------------------------------- */

export type PlatformKind = 'unknown' | 'directory_listing' | 'confirmed_system' | 'direct_website' | 'in_house_system';

export interface LeadOption<V extends string = string> {
  value: V;
  label: string;
  /** Short explanation shown in the edit form (and as the option title). */
  help?: string;
}

export interface LeadOptionConfig {
  platformKinds: ReadonlyArray<LeadOption<PlatformKind>>;
  visitApproaches: ReadonlyArray<LeadOption>;
  statuses: ReadonlyArray<LeadOption>;
  priorities: ReadonlyArray<LeadOption>;
}

/** Current defaults (tenant #1 wording, owner-approved 04-10-2026). Same order as the server lists. */
export const DEFAULT_LEAD_OPTIONS: LeadOptionConfig = {
  platformKinds: [
    { value: 'unknown', label: 'Unknown — ask on visit', help: 'Not found publicly.' },
    { value: 'directory_listing', label: 'Marketplace listing only', help: 'Only bookable via a marketplace page such as Treatwell or Fresha.' },
    { value: 'confirmed_system', label: '3rd-party booking software', help: "Takes bookings through a third-party booking system they don't own (e.g. Fresha, Setmore, Treatwell Connect)." },
    { value: 'direct_website', label: 'Own website, no booking system', help: 'Takes bookings via their own site, phone or email.' },
    { value: 'in_house_system', label: 'In-house booking system', help: 'Their own custom-built booking system (not a third-party product).' },
  ],
  visitApproaches: [
    { value: 'walk_in', label: 'Walk in' },
    { value: 'phone_call', label: 'Phone Call' },
    { value: 'social_fb', label: 'Social media – FB' },
    { value: 'social_ig', label: 'Social media – IG' },
    { value: 'social_tiktok', label: 'Social media – TikTok' },
    { value: 'campaign', label: 'Campaign' },
    { value: 'own_source', label: 'Own Source' },
  ],
  statuses: [
    { value: 'DISCOVERED', label: 'Not visited' },
    { value: 'CONTACTED', label: 'Visited / contacted' },
    { value: 'FOLLOWUP_DUE', label: 'Follow-up due' },
    { value: 'REPLIED_INTERESTED', label: 'Interested' },
    { value: 'REPLIED_NOT_INTERESTED', label: 'Not interested' },
    { value: 'DEMO_BOOKED', label: 'Demo booked' },
    { value: 'SIGNED_UP', label: 'Signed up' },
    { value: 'CONVERTED_TENANT', label: 'Converted' },
    { value: 'QUALIFIED', label: 'Qualified' },
  ],
  priorities: [
    { value: 'A', label: 'Priority A', help: 'Visit first.' },
    { value: 'B', label: 'Priority B' },
    { value: 'C', label: 'Priority C' },
  ],
};

/** A (future) per-workspace override: any list given non-empty REPLACES the default list wholesale. */
export type LeadOptionOverrides = { [K in keyof LeadOptionConfig]?: ReadonlyArray<LeadOption> | null };

export function resolveLeadOptions(overrides?: LeadOptionOverrides | null): LeadOptionConfig {
  if (!overrides) return DEFAULT_LEAD_OPTIONS;
  const pick = <K extends keyof LeadOptionConfig>(k: K): LeadOptionConfig[K] => {
    const o = overrides[k];
    return (o && o.length > 0 ? o : DEFAULT_LEAD_OPTIONS[k]) as LeadOptionConfig[K];
  };
  return { platformKinds: pick('platformKinds'), visitApproaches: pick('visitApproaches'), statuses: pick('statuses'), priorities: pick('priorities') };
}

/** The options in force for this build (defaults until a workspace source exists). */
export const LEAD_OPTIONS: LeadOptionConfig = resolveLeadOptions();

export function optionLabel(list: ReadonlyArray<LeadOption>, value: string | null | undefined, fallback?: string | null): string | null {
  if (value === null || value === undefined || value === '') return fallback ?? null;
  return list.find((o) => o.value === value)?.label ?? fallback ?? value;
}

export function optionHelp(list: ReadonlyArray<LeadOption>, value: string | null | undefined): string | null {
  return list.find((o) => o.value === value)?.help ?? null;
}

export function hasOption(list: ReadonlyArray<LeadOption>, value: string | null | undefined): boolean {
  return !!value && list.some((o) => o.value === value);
}

/* ============================================================================
 * Platform VENDOR catalogue — "filter by name" (owner decision 2026-10-04 19:08).
 * The Platform filter lists real vendor NAMES with counts, grouped:
 *   directory = marketplace / directory listing vendors (Fresha, Treatwell, Booksy, …)
 *   software  = SaaS booking apps (Calendly, Square, Acuity, Setmore, …)
 * The per-lead KIND label (3rd-party booking software / Marketplace listing only / …)
 * and the row display are unchanged — this catalogue only names vendors.
 * The server mirrors this list verbatim in ImportFieldMap::PLATFORM_VENDORS
 * (it filters and counts); the FE unit test asserts the two stay identical.
 * Aliases are lower-case substrings matched against each part of a platform
 * text and against the host of a URL-like part (*.mytreatwell.co.uk → Treatwell).
 * An unrecognised name is kept by its own title-cased name under `software`.
 * ========================================================================== */

export type PlatformGroup = 'directory' | 'software';

export interface PlatformVendor { name: string; group: PlatformGroup; aliases: ReadonlyArray<string> }

export const PLATFORM_VENDORS: ReadonlyArray<PlatformVendor> = [
  { name: 'Treatwell', group: 'directory', aliases: ['treatwell', 'mytreatwell', 'wahanda'] },
  { name: 'Fresha', group: 'directory', aliases: ['fresha', 'shedul'] },
  { name: 'Booksy', group: 'directory', aliases: ['booksy'] },
  { name: 'Bark', group: 'directory', aliases: ['bark'] },
  { name: 'Yell', group: 'directory', aliases: ['yell'] },
  { name: 'Vagaro', group: 'directory', aliases: ['vagaro'] },
  { name: 'StyleSeat', group: 'directory', aliases: ['styleseat'] },
  { name: 'ClassPass', group: 'directory', aliases: ['classpass'] },
  { name: 'Groupon', group: 'directory', aliases: ['groupon'] },
  { name: 'Urban', group: 'directory', aliases: ['urbanmassage', 'urban massage', 'urban.co'] },
  { name: 'Calendly', group: 'software', aliases: ['calendly'] },
  { name: 'Square', group: 'software', aliases: ['square', 'squareup'] },
  { name: 'Acuity', group: 'software', aliases: ['acuity', 'as.me'] },
  { name: 'Setmore', group: 'software', aliases: ['setmore'] },
  { name: 'Timely', group: 'software', aliases: ['timely'] },
  { name: 'Phorest', group: 'software', aliases: ['phorest'] },
  { name: 'Momence', group: 'software', aliases: ['momence'] },
  { name: 'Mindbody', group: 'software', aliases: ['mindbody', 'mind body'] },
  { name: 'SimplyBook', group: 'software', aliases: ['simplybook'] },
  { name: 'Ovatu', group: 'software', aliases: ['ovatu'] },
  { name: 'Zenoti', group: 'software', aliases: ['zenoti'] },
  { name: 'GlossGenius', group: 'software', aliases: ['glossgenius', 'gloss genius'] },
  { name: 'Shore', group: 'software', aliases: ['shore'] },
  { name: 'Salonized', group: 'software', aliases: ['salonized'] },
  { name: 'Wix Bookings', group: 'software', aliases: ['wix'] },
  { name: 'Squarespace', group: 'software', aliases: ['squarespace'] },
  { name: 'Shopify', group: 'software', aliases: ['shopify'] },
  { name: 'Bookeo', group: 'software', aliases: ['bookeo'] },
  { name: 'Appointy', group: 'software', aliases: ['appointy'] },
  { name: 'Boulevard', group: 'software', aliases: ['boulevard', 'joinblvd'] },
  { name: 'Meevo', group: 'software', aliases: ['meevo'] },
  { name: 'Kitomba', group: 'software', aliases: ['kitomba'] },
  { name: 'Schedulicity', group: 'software', aliases: ['schedulicity'] },
  { name: 'Jane', group: 'software', aliases: ['janeapp', 'jane app', 'jane.app'] },
  { name: 'Cliniko', group: 'software', aliases: ['cliniko'] },
  { name: 'Pabau', group: 'software', aliases: ['pabau'] },
  { name: 'Timify', group: 'software', aliases: ['timify'] },
  { name: 'Goldie', group: 'software', aliases: ['goldie', 'appointfix'] },
  { name: 'Resurva', group: 'software', aliases: ['resurva'] },
  { name: 'Nabooki', group: 'software', aliases: ['nabooki'] },
  { name: 'YouCanBookMe', group: 'software', aliases: ['youcanbook'] },
  { name: 'Zettle', group: 'software', aliases: ['zettle', 'izettle'] },
  { name: 'Booksy Biz', group: 'software', aliases: ['booksy biz', 'booksy.biz'] },
];

export const PLATFORM_GROUP_LABEL: Record<PlatformGroup, { title: string; all: string }> = {
  directory: { title: 'Platform / directory', all: 'All platforms / directories' },
  software: { title: 'Booking software', all: 'All booking software' },
};

/** Parts of a platform text that are not vendor names (mirror of the server regex). */
const NON_VENDOR_PART_RX = /^(unknown|unclear|not known|tbc|tbd|\?|none|n\/a|na|-|—|own[\s-]?(domain|site|website)( booking)?|direct booking|website booking|in[\s-]?house( system| booking)?|custom( system| booking)?|bespoke|own system|email|phone|walk[\s-]?in|instagram|facebook|dm|whatsapp)\b/i;

function hostOf(part: string): string {
  try { return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(part) ? part : `http://${part}`).hostname.replace(/^www\./, '').toLowerCase(); } catch { return part.toLowerCase(); }
}

/** Canonical vendor for a lower-cased part/host, or null when unrecognised. */
export function vendorFor(lower: string): string | null {
  // Longest alias wins ("booksy biz" → Booksy Biz, not Booksy; "squarespace" → Squarespace, not Square).
  // Word-boundary match ("Yellow Pages" is not Yell, "Embark" is not Bark); '.', '/', ' ' etc. are boundaries so hosts still resolve.
  let best: string | null = null; let bestLen = 0;
  for (const v of PLATFORM_VENDORS) for (const a of v.aliases) {
    if (a.length <= bestLen) continue;
    const rx = new RegExp(`(^|[^a-z0-9])${a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-z0-9])`);
    if (lower === a || rx.test(lower)) { best = v.name; bestLen = a.length; }
  }
  return best;
}

/** Canonical vendor names in a platform text ("Setmore + Treatwell" → ["Setmore", "Treatwell"]; "Unknown — ask owner" → []). */
export function platformNames(text: string | null | undefined): string[] {
  let t = (text ?? '').trim();
  if (!t) return [];
  t = t.replace(/\s*[—–-]\s*ask (the )?owner\.?$/i, '').replace(/\s*\((ask (the )?owner|tbc)\)$/i, '').trim();
  // URL tokens collapse to their host BEFORE splitting (the path's "/" is not a separator).
  t = t.replace(/https?:\/\/\S+|\b[a-z0-9-]+(\.[a-z0-9-]+)+\.[a-z]{2,}(\/\S*)?/gi, (m) => hostOf(m));
  const out: string[] = [];
  for (let part of t.split(/\s*(?:\+|,|&|\band\b|\/|;|\bor\b|\bvia\b|\bthen\b)\s*/i)) {
    part = part.replace(/^["'()\s]+|["'()\s]+$/g, '');
    if (!part || NON_VENDOR_PART_RX.test(part)) continue;
    let lower = part.toLowerCase();
    if (/^(https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(part)) lower = hostOf(part);
    let name = vendorFor(lower);
    if (!name) name = lower.replace(/^www\./, '').replace(/\s+/g, ' ').trim().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
    if (name && !out.includes(name)) out.push(name);
  }
  return out;
}

/** Group of a canonical name (unrecognised → software). */
export function platformGroup(name: string): PlatformGroup {
  return PLATFORM_VENDORS.find((v) => v.name === name)?.group ?? 'software';
}

/* ============================================================================
 * Lead ACTION switches (owner decision 04-10-2026: "disable phone calling on
 * desktop for now for lead — not needed atm"). ONE flag per action/surface so a
 * behaviour can be turned back on in one place. The phone NUMBER itself always
 * stays visible and editable; only the tel: action (Call button / tel link) is
 * governed here. "Desktop" = the app's lg breakpoint AND a hover-capable fine
 * pointer (see useIsDesktopPointer) — a phone/tablet in a wide window keeps
 * its Call action.
 * ========================================================================== */
export const LEAD_ACTIONS = {
  /** Show the Call (tel:) action on desktop surfaces (table row, detail Phone link). false = hidden for now. */
  callOnDesktop: false,
} as const;

/** Pure rule: may the Call (tel:) action be offered on a surface? */
export function callActionAllowed(isDesktopPointer: boolean, flags: { callOnDesktop: boolean } = LEAD_ACTIONS): boolean {
  return isDesktopPointer ? flags.callOnDesktop : true;
}

/* ============================================================================
 * Lead CATEGORIES (item 7, 05-10-2026) — "filter leads by pilates, yoga, therapy…".
 * LEAD_CATEGORY_DATA is the ONE FE copy of: the versioned snapshot of the BookSesh
 * marketplace categories (booksesh.com Voxel taxonomy `places_category`, read-only
 * from the public /session/ search page — the site's REST API does not expose it),
 * the default workspace list (seed) and the business-type → category rules.
 * It is mirrored EXACTLY by the server (LeadCategories::DATA, parity-tested by the
 * import suite — the block between the parity markers must stay strict JSON).
 * The workspace's live list (owner add / rename / archive / merge / re-sync) comes
 * from GET /outreach/lead-categories; this seed is only the default + fallback.
 * Unknown business types map to "other" — a lead is never left uncategorised.
 * ========================================================================== */
/** booksesh = BookSesh marketplace (session) category; booksesh_events = BookSesh events category; lead = derived from
 *  the imported leads' business types; workspace = added here by the owner; system = Other. */
export type LeadCategorySource = 'booksesh' | 'booksesh_events' | 'lead' | 'workspace' | 'system';
export interface LeadCategoryTerm { slug: string; name: string; parent: string | null }
export interface LeadCategorySeedItem { slug: string; name: string; source: LeadCategorySource }
export interface LeadCategoryData {
  snapshot: { version: string; fetched_at: string; source: string; terms: LeadCategoryTerm[]; events_source: string; event_terms: LeadCategoryTerm[] };
  seed: LeadCategorySeedItem[];
  rules: Record<string, string[]>;
  /** aliases never read from a business NAME */
  name_stop: string[];
  /** studio disciplines a business name narrows (a pilates studio -> Pilates only) */
  disciplines: string[];
  /** generic words that decide a part only when no more specific alias matches */
  weak_aliases: string[];
}

// @parity:lead-categories:start
export const LEAD_CATEGORY_DATA: LeadCategoryData = {
  "snapshot": {
    "version": "2026-10-05b",
    "fetched_at": "2026-10-05T11:48:00Z",
    "source": "https://booksesh.com/session/ (Voxel places_category search filter, public page)",
    "terms": [
      {
        "slug": "spiritual",
        "name": "Spiritual",
        "parent": null
      },
      {
        "slug": "readings-guidance",
        "name": "Readings & Guidance",
        "parent": "spiritual"
      },
      {
        "slug": "therapy",
        "name": "Therapy & Counselling",
        "parent": null
      },
      {
        "slug": "holistic-treatments",
        "name": "Holistic Treatments",
        "parent": null
      },
      {
        "slug": "kundalini-activation",
        "name": "Kundalini Activation",
        "parent": "holistic-treatments"
      },
      {
        "slug": "energy-healing",
        "name": "Energy healing",
        "parent": "holistic-treatments"
      },
      {
        "slug": "reiki-healing",
        "name": "Reiki Healing",
        "parent": "holistic-treatments"
      },
      {
        "slug": "shamanic-healing",
        "name": "Shamanic Healing",
        "parent": "holistic-treatments"
      },
      {
        "slug": "sound-healing",
        "name": "Sound Healing",
        "parent": "holistic-treatments"
      }
    ],
    "events_source": "https://booksesh.com/events-in/london/ (Voxel events-category search filter, public page; catch-all term \"Events\" excluded)",
    "event_terms": [
      {
        "slug": "art",
        "name": "Art",
        "parent": null
      },
      {
        "slug": "beauty",
        "name": "Beauty",
        "parent": null
      },
      {
        "slug": "business",
        "name": "Business",
        "parent": null
      },
      {
        "slug": "community",
        "name": "Community",
        "parent": null
      },
      {
        "slug": "fashion",
        "name": "Fashion",
        "parent": null
      },
      {
        "slug": "fitness",
        "name": "Fitness",
        "parent": null
      },
      {
        "slug": "music",
        "name": "Music",
        "parent": null
      },
      {
        "slug": "spirituality",
        "name": "Spirituality",
        "parent": null
      },
      {
        "slug": "wellness",
        "name": "Wellness",
        "parent": null
      },
      {
        "slug": "cacao-ceremony",
        "name": "Cacao Ceremony",
        "parent": "wellness"
      },
      {
        "slug": "sound-bath",
        "name": "Sound Bath",
        "parent": "wellness"
      }
    ]
  },
  "seed": [
    {
      "slug": "spiritual",
      "name": "Spiritual",
      "source": "booksesh"
    },
    {
      "slug": "readings-guidance",
      "name": "Readings & Guidance",
      "source": "booksesh"
    },
    {
      "slug": "therapy",
      "name": "Therapy & Counselling",
      "source": "booksesh"
    },
    {
      "slug": "holistic-treatments",
      "name": "Holistic Treatments",
      "source": "booksesh"
    },
    {
      "slug": "kundalini-activation",
      "name": "Kundalini Activation",
      "source": "booksesh"
    },
    {
      "slug": "energy-healing",
      "name": "Energy healing",
      "source": "booksesh"
    },
    {
      "slug": "reiki-healing",
      "name": "Reiki Healing",
      "source": "booksesh"
    },
    {
      "slug": "shamanic-healing",
      "name": "Shamanic Healing",
      "source": "booksesh"
    },
    {
      "slug": "sound-healing",
      "name": "Sound Healing",
      "source": "booksesh"
    },
    {
      "slug": "beauty",
      "name": "Beauty",
      "source": "booksesh_events"
    },
    {
      "slug": "wellness",
      "name": "Wellness",
      "source": "booksesh_events"
    },
    {
      "slug": "fitness",
      "name": "Fitness",
      "source": "booksesh_events"
    },
    {
      "slug": "spirituality",
      "name": "Spirituality",
      "source": "booksesh_events"
    },
    {
      "slug": "sound-bath",
      "name": "Sound Bath",
      "source": "booksesh_events"
    },
    {
      "slug": "cacao-ceremony",
      "name": "Cacao Ceremony",
      "source": "booksesh_events"
    },
    {
      "slug": "art",
      "name": "Art",
      "source": "booksesh_events"
    },
    {
      "slug": "business",
      "name": "Business",
      "source": "booksesh_events"
    },
    {
      "slug": "community",
      "name": "Community",
      "source": "booksesh_events"
    },
    {
      "slug": "fashion",
      "name": "Fashion",
      "source": "booksesh_events"
    },
    {
      "slug": "music",
      "name": "Music",
      "source": "booksesh_events"
    },
    {
      "slug": "hair",
      "name": "Hair",
      "source": "lead"
    },
    {
      "slug": "nails",
      "name": "Nails",
      "source": "lead"
    },
    {
      "slug": "aesthetics",
      "name": "Aesthetics",
      "source": "lead"
    },
    {
      "slug": "barber",
      "name": "Barber & Grooming",
      "source": "lead"
    },
    {
      "slug": "massage",
      "name": "Massage",
      "source": "lead"
    },
    {
      "slug": "physio",
      "name": "Physio",
      "source": "lead"
    },
    {
      "slug": "pilates",
      "name": "Pilates",
      "source": "lead"
    },
    {
      "slug": "yoga",
      "name": "Yoga",
      "source": "lead"
    },
    {
      "slug": "other",
      "name": "Other",
      "source": "system"
    }
  ],
  "rules": {
    "spiritual": [
      "spiritual"
    ],
    "readings-guidance": [
      "tarot",
      "tarot reading",
      "psychic",
      "psychic medium",
      "oracle",
      "astrology",
      "reading",
      "readings"
    ],
    "therapy": [
      "therapy",
      "therapist",
      "counselling",
      "counseling",
      "counsellor",
      "psychotherapy",
      "hypnotherapy"
    ],
    "holistic-treatments": [
      "holistic",
      "holistic treatments",
      "holistic therapy",
      "holistic therapist",
      "breathwork",
      "kambo"
    ],
    "kundalini-activation": [
      "kundalini"
    ],
    "energy-healing": [
      "energy healing"
    ],
    "reiki-healing": [
      "reiki"
    ],
    "shamanic-healing": [
      "shamanic",
      "shaman"
    ],
    "sound-healing": [
      "sound healing"
    ],
    "beauty": [
      "beauty",
      "beauty therapy",
      "beauty therapist",
      "beauty therapists",
      "beauty salon",
      "waxing",
      "wax",
      "lashes",
      "lash",
      "brows",
      "brow",
      "make up",
      "makeup",
      "make-up",
      "tanning",
      "spray tan",
      "threading"
    ],
    "wellness": [
      "wellness",
      "wellbeing",
      "well-being",
      "spa",
      "day spa"
    ],
    "fitness": [
      "fitness",
      "movement",
      "recovery",
      "gym",
      "personal training",
      "personal trainer",
      "bootcamp"
    ],
    "spirituality": [
      "spirituality"
    ],
    "sound-bath": [
      "sound bath",
      "gong",
      "gong bath",
      "singing bowl",
      "singing bowls"
    ],
    "cacao-ceremony": [
      "cacao",
      "cacao ceremony"
    ],
    "hair": [
      "hair",
      "hairdresser",
      "hairdressers",
      "hairdressing",
      "hair salon",
      "blow dry",
      "blowdry",
      "colourist",
      "colorist"
    ],
    "nails": [
      "nails",
      "nail",
      "manicure",
      "pedicure"
    ],
    "aesthetics": [
      "aesthetics",
      "aesthetic",
      "botox",
      "fillers",
      "skin clinic",
      "skincare",
      "skin care",
      "facial",
      "facials"
    ],
    "barber": [
      "barber",
      "barbers",
      "barbershop",
      "grooming"
    ],
    "massage": [
      "massage",
      "sports massage",
      "bodywork"
    ],
    "physio": [
      "physio",
      "physiotherapy",
      "physiotherapist",
      "osteopath",
      "osteopathy",
      "chiropractor",
      "chiropractic",
      "sports therapy"
    ],
    "pilates": [
      "pilates",
      "reformer",
      "body control"
    ],
    "yoga": [
      "yoga"
    ]
  },
  "name_stop": [
    "reading",
    "readings",
    "movement",
    "recovery",
    "wax",
    "brow",
    "brows",
    "lash",
    "lashes",
    "oracle",
    "therapy",
    "therapist"
  ],
  "disciplines": [
    "pilates",
    "yoga"
  ],
  "weak_aliases": [
    "therapy",
    "therapist",
    "therapists"
  ]
};
// @parity:lead-categories:end

export const OTHER_CATEGORY = 'other';

/** Category slugs for a business-type text — mirror of LeadCategories::fromText (parity-tested on the 8 workbook types). */
export function leadCategoriesFor(text: string | null | undefined, data: LeadCategoryData = LEAD_CATEGORY_DATA): string[] {
  const t = (text ?? '').trim().toLowerCase();
  const out: string[] = [];
  if (t) {
    for (let part of t.split(/\s*(?:\/|,|&|\+|;|\band\b|\bor\b)\s*/)) {
      part = part.replace(/^[\s"'()\-.]+|[\s"'()\-.]+$/g, '');
      if (!part) continue;
      // Specific aliases first; weak generic words (therapy / therapist) only when nothing more specific matches.
      let best: string | null = null;
      for (const weakPass of [false, true]) {
        let bestLen = 0;
        for (const [slug, aliases] of Object.entries(data.rules)) for (const a of aliases) {
          if (data.weak_aliases.includes(a) !== weakPass) continue;
          if (a.length <= bestLen) continue;
          const rx = new RegExp(`(^|[^a-z0-9])${a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-z0-9])`);
          if (part === a || rx.test(part)) { best = slug; bestLen = a.length; }
        }
        if (best) break;
      }
      if (best && !out.includes(best)) out.push(best);
    }
  }
  return out.length ? out : [OTHER_CATEGORY];
}

/** Every category NAMED in a business name — mirror of LeadCategories::matchName (all non-overlapping alias hits,
 *  longest first; name_stop aliases skipped; discipline words also match glued, e.g. "Pureflowyoga"). */
export function leadCategoryNameMatches(name: string | null | undefined, data: LeadCategoryData = LEAD_CATEGORY_DATA): string[] {
  const n = (name ?? '').trim().toLowerCase();
  if (!n) return [];
  const cands: Array<[number, number, string]> = [];
  for (const [slug, aliases] of Object.entries(data.rules)) for (const a of aliases) {
    if (data.name_stop.includes(a)) continue;
    const esc = a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rx = data.disciplines.includes(a) ? new RegExp(esc, 'g') : new RegExp(`(?<![a-z0-9])${esc}(?![a-z0-9])`, 'g');
    for (const m of n.matchAll(rx)) cands.push([m.index ?? 0, a.length, slug]);
  }
  cands.sort((x, y) => y[1] - x[1] || x[0] - y[0]);
  const taken = new Set<number>(); const hits: Array<[number, string]> = [];
  for (const [off, len, slug] of cands) {
    let free = true; for (let i = off; i < off + len; i++) if (taken.has(i)) { free = false; break; }
    if (!free) continue;
    for (let i = off; i < off + len; i++) taken.add(i);
    hits.push([off, slug]);
  }
  hits.sort((x, y) => x[0] - y[0]);
  const out: string[] = [];
  for (const [, slug] of hits) if (!out.includes(slug)) out.push(slug);
  return out;
}

/** A lead's AUTOMATIC categories from business TYPE + NAME — mirror of LeadCategories::fromLead (see there). */
export function leadCategoriesForLead(type: string | null | undefined, name: string | null | undefined, data: LeadCategoryData = LEAD_CATEGORY_DATA): string[] {
  const t = leadCategoriesFor(type, data).filter((x) => x !== OTHER_CATEGORY);
  const nm = leadCategoryNameMatches(name, data);
  const disc = nm.filter((x) => data.disciplines.includes(x));
  let out: string[];
  if (disc.length) {
    if (!t.some((x) => nm.includes(x))) out = nm;
    else out = [...new Set([...t, ...nm])].filter((x) => !data.disciplines.includes(x) || disc.includes(x));
  } else out = [...new Set([...t, ...nm])];
  return out.length ? out : [OTHER_CATEGORY];
}
